import {
  IonButton,
  IonChip,
  IonHeader,
  IonIcon,
  IonLabel,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import React from 'react';
import { iTrack } from '../../types/ITrack';
import { EditableBPMField } from './EditableBPMField';
import { EditableKeyField } from './EditableKeyField';
import { EditableTextField } from './EditableTextField';
import './TrackMetadataPanel.scss';

interface TrackMetadataPanelProps {
  track: iTrack;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedTrack: iTrack) => void;
}

export const TrackMetadataPanel: React.FC<TrackMetadataPanelProps> = ({
  track,
  isOpen,
  onClose,
  onSave,
}) => {
  const [editedTrack, setEditedTrack] = React.useState<iTrack>(track);

  const handleSave = () => {
    onSave(editedTrack);
    onClose();
  };

  // Use effect to update editedTrack when the input track changes
  React.useEffect(() => {
    setEditedTrack(track);
  }, [track]);

  if (!isOpen || !track) return null; // Added check for track existence

  // Helper function to render metadata rows
  const renderMetadataRow = (label: string, value: any) => {
    if (value === null || value === undefined || value === '') return null; // Don't render if value is empty
    return (
      <div className="metadata-row">
        <span className="metadata-label">{label}</span>
        <span className="metadata-value">{String(value)}</span>
      </div>
    );
  };

  return (
    <div className="track-metadata-panel-overlay" onClick={onClose}>
      {' '}
      {/* Close on overlay click */}
      <div
        className="track-metadata-panel"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside panel
      >
        <IonHeader className="panel-header">
          <IonToolbar>
            {/* Display track Title prominently */}
            <IonTitle className="panel-title">
              {track.Title || 'Track Details'}
            </IonTitle>
            <IonButton
              slot="end"
              fill="clear"
              onClick={onClose}
              className="close-button"
            >
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonToolbar>
          {/* Display Artist below Title */}
          {track.Artist && <div className="panel-subtitle">{track.Artist}</div>}
        </IonHeader>

        <div className="panel-content">
          {/* --- Primary Info Section --- */}
          <div className="metadata-section">
            {renderMetadataRow('Album', track.Album)}
          </div>

          {/* --- Tags Section --- */}
          <div className="metadata-section tags-container">
            {track.Genre && (
              <IonChip className="tag-chip genre-tag">
                <IonLabel>{track.Genre}</IonLabel>
              </IonChip>
            )}
            {track.Year && (
              <IonChip className="tag-chip year-tag">
                <IonLabel>{track.Year}</IonLabel>
              </IonChip>
            )}
            {track.Key && (
              <IonChip className="tag-chip key-tag">
                <IonLabel>{track.Key}</IonLabel>
              </IonChip>
            )}
          </div>

          {/* --- Technical Info Section --- */}
          <div className="metadata-section">
            {renderMetadataRow('BPM', track.BPM)}
            {renderMetadataRow('Bitrate', track.Bitrate)}
            {renderMetadataRow('File Path', track.FilePath)}
            {renderMetadataRow('Import Source', track.ImportSources)}
            {renderMetadataRow('ID', track.ID)}
            {/* Add other relevant fields */}
          </div>

          {/* --- Edit Section --- */}
          <div className="metadata-section edit-section">
            <h4 className="edit-title">Edit Fields</h4>
            {/* Keep editable fields grouped */}
            <EditableTextField
              trackId={track.ID}
              text={editedTrack.Title}
              field="Title"
              textColor="dark"
            />
            <EditableTextField
              trackId={track.ID}
              text={editedTrack.Artist}
              field="Artist"
              textColor="dark"
            />
            <EditableBPMField
              trackId={track.ID}
              bpmVal={editedTrack.BPM}
              textColor="dark"
            />
            <EditableKeyField
              trackId={track.ID}
              keyVal={editedTrack.Key?.toString()}
              textColor="dark"
            />
            <IonButton
              expand="block"
              onClick={handleSave}
              className="save-button"
            >
              Save Changes
            </IonButton>
          </div>

          {/* --- Raw Data (Optional/Debug) --- */}
          {/* Consider removing or hiding this behind a toggle for cleaner UI */}
          {/*
          <div className="metadata-section raw-data-section">
            <h4 className="raw-data-title">Raw Metadata</h4>
            <table className="raw-data-table">
              <tbody>
                {Object.entries(track).map(([key, value]) => (
                  <tr key={key}>
                    <td className="raw-data-key">{key}</td>
                    <td className="raw-data-value">
                      {typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
           */}
        </div>
      </div>
    </div>
  );
};
